"use client";

import {
  AGE_GROUP_CHANGED_EVENT,
  AGE_GROUP_KEY,
  type AgeGroup,
  getAgeGroupFromStorage,
} from "@/lib/age-group/store";
import {
  NPS_COMPLETED_KEY,
  NPS_SESSION_CHANGED_EVENT,
  NPS_SURVEY_FLAG_KEY,
} from "@/lib/nps/constants";
import { isInNpsSurveyCohort } from "@/lib/nps/flag";
import { hasPriorDayNpsSession } from "@/lib/nps/prior-day-session";
import { readNpsTimerStartedAt } from "@/lib/nps/session";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { useEffect, useState } from "react";

const NPS_TIMER_DURATION = 15 * 60 * 1000; // 15 minutes

interface UseNPSTimerReturn {
  shouldShowNPS: boolean;
  dismissNPS: () => void;
}

function wasNpsCompleted(): boolean {
  try {
    return localStorage.getItem(NPS_COMPLETED_KEY) === "true";
  } catch {
    return false;
  }
}

export function useNPSTimer(): UseNPSTimerReturn {
  const [shouldShowNPS, setShouldShowNPS] = useState(false);
  const [ageGroup, setAgeGroup] = useState<AgeGroup | null>(null);
  /** PostHog `nps-survey` (~10% for kids/unknown). Undefined until flags load — fail closed for non-grownups. */
  const npsSurveyFlag = useFeatureFlagEnabled(NPS_SURVEY_FLAG_KEY);

  useEffect(() => {
    const refreshAge = () => setAgeGroup(getAgeGroupFromStorage());
    refreshAge();

    const onAgeGroupChanged = () => refreshAge();
    const onStorage = (e: StorageEvent) => {
      if (e.key === AGE_GROUP_KEY) refreshAge();
    };

    window.addEventListener(AGE_GROUP_CHANGED_EVENT, onAgeGroupChanged);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(AGE_GROUP_CHANGED_EVENT, onAgeGroupChanged);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;

    const clearTimer = () => {
      if (timer !== undefined) {
        clearTimeout(timer);
        timer = undefined;
      }
    };

    const evaluate = () => {
      clearTimer();

      // Cohort (grownup 100% / others ~10%) + prior calendar-day sessions + not already completed.
      if (
        wasNpsCompleted() ||
        !isInNpsSurveyCohort(npsSurveyFlag, ageGroup) ||
        !hasPriorDayNpsSession()
      ) {
        setShouldShowNPS(false);
        return;
      }

      const startedAt = readNpsTimerStartedAt();
      if (startedAt == null) {
        setShouldShowNPS(false);
        return;
      }

      const remaining = NPS_TIMER_DURATION - (Date.now() - startedAt);
      if (remaining <= 0) {
        setShouldShowNPS(true);
        return;
      }

      setShouldShowNPS(false);
      timer = setTimeout(() => {
        if (
          wasNpsCompleted() ||
          !isInNpsSurveyCohort(npsSurveyFlag, ageGroup) ||
          !hasPriorDayNpsSession()
        ) {
          setShouldShowNPS(false);
          return;
        }
        setShouldShowNPS(true);
      }, remaining);
    };

    window.addEventListener(NPS_SESSION_CHANGED_EVENT, evaluate);
    evaluate();

    return () => {
      clearTimer();
      window.removeEventListener(NPS_SESSION_CHANGED_EVENT, evaluate);
    };
  }, [npsSurveyFlag, ageGroup]);

  const dismissNPS = () => {
    try {
      localStorage.setItem(NPS_COMPLETED_KEY, "true");
    } catch (error) {
      console.error("Error persisting NPS completion", { error });
    }
    // Always hide even if storage is unavailable — otherwise dismiss appears broken.
    setShouldShowNPS(false);
  };

  return {
    shouldShowNPS,
    dismissNPS,
  };
}
